import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { API_ENDPOINT, PAGE_SIZE } from "../../constants/constants";
import { getDateFormat, getHeader, getDifferenceInDays } from "../../utils/util";
import "./membership.css";
import CustomCircularP from "../widgets/customCircularP";
import * as XLSX from "xlsx";
import MyModal from "../widgets/myModal/myModal";
import PlanHistory from "./PlanHistory";
import TopUp from "./TopUp";
import SetPlan from "./SetPlan";
import MembershipTrend from "./MembershipTrend";

const ExportIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
)

const Membership = () => {
  const [currentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [planHistory, setPlanHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [status, setStatus] = useState("Active");
  const [showHistory, setShowHistory] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpUser, setTopUpUser] = useState("");
  const [showSetPlan, setShowSetPlan] = useState(false);
  const [planUser, setPlanUser] = useState("");
  const [trendUser, setTrendUser] = useState(null);

  const viewPlanHistory = (phone) => {
    getPlanHistory(phone);
    setShowHistory(true);
  };

  function getPlanHistory(phone) {
    setLoading(true);
    axios.post(API_ENDPOINT + "admin/transaction-history", { phone }, getHeader()).then((res) => {
      let d = res.data;
      if (d.success) { setPlanHistory(d.message); }
      setLoading(false);
    });
  }

  useEffect(() => { getMembers(); }, [searchQ, status]);

  function getMembers() {
    setLoading(true);
    axios.post(API_ENDPOINT + "admin/get-trainers", { page: currentPage, searchQ, status }, getHeader()).then((res) => {
      setLoading(false);
      let d = res.data;
      if (d.success) {
        setUsers(d.message);
        // Keep an open trend view pointed at the freshly-loaded document so a
        // credited top-up shows up without leaving the page.
        setTrendUser((cur) => cur ? (d.message.find(u => u.phone === cur.phone) || cur) : cur);
      } else { toast(d.message); }
    });
  }

  const remainingOf = (plan) => {
    if (!plan) return "—";
    if (plan.status === "Expired") {
      return plan.endsOn ? `Expired ${getDateFormat(new Date(plan.endsOn))}` : "Expired";
    }
    return getDifferenceInDays(new Date(), plan.endsOn);
  };

  const onExport = () => {
    const newUsers = users.map((user) => ({
      name: user.name,
      email: user.email,
      phone: user.phone,
      plan: user.plan ? user.plan.name : "",
      subscribedOn: user.plan ? getDateFormat(new Date(user.plan.createdOn)) : "",
      remaining: remainingOf(user.plan),
      joinedOn: getDateFormat(new Date(user.createdOn)),
      lastUsedOn: user.plan && user.plan.usedDate ? getDateFormat(new Date(user.plan.usedDate)) : "",
    }));
    const ws = XLSX.utils.json_to_sheet(newUsers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "output.xlsx");
  };

  const onTopUp = (user) => { setTopUpUser(user); setShowTopUp(true); };
  const onSetPlan = (user) => { setPlanUser(user); setShowSetPlan(true); };

  if (trendUser) {
    return (
      <div>
        <CustomCircularP show={loading} />
        <MembershipTrend
          user={trendUser}
          onBack={() => setTrendUser(null)}
          onChanged={getMembers}
        />
      </div>
    );
  }

  return (
    <div>
      <CustomCircularP show={loading} />

      {/* TOOLBAR */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, height: 38, padding: "0 12px", background: "var(--color-surface)", border: "1px solid var(--color-divider)", flex: 1, maxWidth: 300, color: "var(--color-neutral-500)" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search by name…"
            style={{ border: 0, background: "transparent", outline: "none", font: "inherit", fontSize: 13, color: "var(--color-text)", width: "100%" }}
          />
        </div>
        <div className="seg">
          <label className="seg-opt">
            <input type="radio" name="mstatus" checked={status === "Active"} onChange={() => setStatus("Active")} />
            Active
          </label>
          <label className="seg-opt">
            <input type="radio" name="mstatus" checked={status === "Expired"} onChange={() => setStatus("Expired")} />
            Expired
          </label>
        </div>
        <div style={{ flex: 1 }}></div>
        <button className="btn btn-secondary" onClick={onExport}><ExportIcon />Export</button>
      </div>

      {/* TABLE */}
      <div style={{ background: "var(--color-surface)", padding: "6px 18px 12px" }}>
        <table className="table">
          <thead>
            <tr>
              <th>#</th><th>Name</th><th>Email</th><th>Phone</th><th>Plan</th>
              <th>Remaining</th><th>Joined</th><th>Last used</th><th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((od, i) => (
              <tr key={od._id || i}>
                <td className="text-muted">{(currentPage - 1) * PAGE_SIZE + i + 1}</td>
                <td style={{ fontWeight: 600 }}>{od.name}</td>
                <td className="text-muted">{od.email}</td>
                <td>{od.phone}</td>
                <td>
                  {od.plan
                    ? <span className={od.plan.status === "Expired" ? "tag tag-neutral" : "tag tag-accent"}>{od.plan.name}</span>
                    : <span className="tag tag-neutral">Not subscribed</span>}
                </td>
                <td>{remainingOf(od.plan)}</td>
                <td className="text-muted">{getDateFormat(new Date(od.createdOn))}</td>
                <td className="text-muted">{od.plan && od.plan.usedDate ? getDateFormat(new Date(od.plan.usedDate)) : "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => setTrendUser(od)}>Trend</button>
                  <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => viewPlanHistory(od.phone)}>Transactions</button>
                  <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => onTopUp(od)}>Top up</button>
                  <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => onSetPlan(od)}>Set plan</button>
                </td>
              </tr>
            ))}
            {users.length === 0 && !loading && (
              <tr><td colSpan={9} className="text-muted" style={{ textAlign: "center", padding: 24 }}>No {status.toLowerCase()} trainers found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <MyModal
        showModal={showHistory}
        title="All Transactions"
        setShowModal={setShowHistory}
        modalC={<PlanHistory show={showHistory} onActionDone="" mode="" planHistory={planHistory} />}
      />
      <MyModal
        showModal={showTopUp}
        title="Top Up"
        setShowModal={setShowTopUp}
        modalC={<TopUp show={showTopUp} user={topUpUser} />}
      />
      <MyModal
        showModal={showSetPlan}
        title="Set subscription"
        setShowModal={setShowSetPlan}
        modalC={
          <SetPlan
            show={showSetPlan}
            user={planUser}
            onDone={() => { setShowSetPlan(false); getMembers(); }}
          />
        }
      />
    </div>
  );
};
export default Membership;
